import {
    FILE_TYPE_EXTENSIONS,
    FILE_TYPE_MIME_TYPES,
    FileType,
} from "@common/constants/financial-documents";

const GENERIC_CONTENT_TYPES = new Set([
    "application/octet-stream",
    "binary/octet-stream",
]);

const FALLBACK_CONTENT_TYPE = "application/octet-stream";

const EXTENSION_TO_CONTENT_TYPE = new Map(
    Object.values(FileType).map((fileType) => [
        FILE_TYPE_EXTENSIONS[fileType].slice(1).toLowerCase(),
        FILE_TYPE_MIME_TYPES[fileType][0],
    ]),
);

function fileExtension(fileName: string): string | undefined {
    return /\.([a-z0-9]+)$/i.exec(fileName.trim())?.[1].toLowerCase();
}

export function isGenericContentType(
    contentType: string | null | undefined,
): boolean {
    return (
        !contentType?.trim() ||
        GENERIC_CONTENT_TYPES.has(contentType.trim().toLowerCase())
    );
}

interface PreferSpecificContentTypeParams {
    preferred: string | null | undefined;
    fallback: string;
}

export function preferSpecificContentType({
    preferred,
    fallback,
}: PreferSpecificContentTypeParams): string {
    return preferred && !isGenericContentType(preferred) ? preferred : fallback;
}

export function supportedExtensionFromFileName(
    fileName: string,
): string | undefined {
    const extension = fileExtension(fileName);
    return extension && EXTENSION_TO_CONTENT_TYPE.has(extension)
        ? extension
        : undefined;
}

// Gated to supported extensions so a mislabeled attachment can't be promoted
// to a renderable type (e.g. text/html, image/svg+xml).
export function contentTypeFromFileName(fileName: string): string | undefined {
    const extension = supportedExtensionFromFileName(fileName);
    return extension && EXTENSION_TO_CONTENT_TYPE.get(extension);
}

interface NormalizeContentTypeParams {
    contentType: string | null | undefined;
    fileName: string;
}

/**
 * Mail clients (notably Outlook) often label attachments as a generic binary
 * type, which downstream previewers can't recognize. When that happens we trust
 * the filename extension instead.
 */
export function normalizeContentType({
    contentType,
    fileName,
}: NormalizeContentTypeParams): string {
    return preferSpecificContentType({
        preferred: contentType,
        fallback:
            contentTypeFromFileName(fileName) ??
            contentType?.trim() ??
            FALLBACK_CONTENT_TYPE,
    });
}
